import React, { Component } from 'react';
import { Form, Field } from 'react-final-form';
import { FORM_ERROR } from 'final-form';
import { v4 as uuidv4 } from 'uuid';
import isEqual from 'lodash/isEqual';
import { connect } from 'react-redux';
import { TextField, ClassificatorField } from '../../atoms';
import consumerTypeClassificator from '../../constants/classificators/consumerType';
import { Row, Col, Button, Spin } from 'antd';
import { FormActions } from '../../components';
import { FORM_ADD_CONSUMER } from '../../constants/formKeys';
import { consumerFields } from '../../constants/consumer';
import { addConsumer } from '../../actions/actionCreators/consumerAddActionCreators';
import { getAddConsumer } from '../../reducers/consumerReducer';
import { withTranslation } from 'react-i18next';
import { NAMESPACES } from '../../i18n';
import PropTypes from 'prop-types';
import { getLoadingSelector } from '../../utils/asyncHelpers';
import { parseNumber } from '../../utils/formHelpers';
import FormStateToRedux from '../FormStateToRedux';
import { makeRffValidateFromLegacy } from '../rffLegacyValidateAdapter';
import validateSchema from './validateSchema';

const AddConsumerCompany = ({ t, isAdmin, isEdit }) => (
  <React.Fragment>
    <Row gutter={32}>
      <Col span={12}>
        <Field
          name={consumerFields.COMPANY_NAME}
          component={TextField}
          type="text"
          required
          label={t('consumer.companyNameLabel')}
          disabled={!isAdmin && isEdit}
        />
      </Col>
      <Col span={12}>
        <Field
          component={TextField}
          name={consumerFields.EICX_CODE}
          type="text"
          label={t('consumer.eicxCode')}
          required
          disabled={!isAdmin && isEdit}
        />
      </Col>
    </Row>
    <Row gutter={32}>
      <Col span={12}>
        <Field
          name={consumerFields.REGISTER_CODE}
          component={TextField}
          type="text"
          label={t('consumer.registerCodeLabel')}
          required
          parse={parseNumber}
          disabled={!isAdmin && isEdit}
        />
      </Col>
    </Row>
  </React.Fragment>
);

const AddConsumerPerson = ({ t, isAdmin, isEdit }) => (
  <React.Fragment>
    <Row gutter={32}>
      <Col span={12}>
        <Field
          name={consumerFields.NAME}
          component={TextField}
          type="text"
          label={t('consumer.nameLabel')}
          required
          disabled={!isAdmin && isEdit}
        />
      </Col>
      <Col span={12}>
        <Field
          name={consumerFields.EICX_CODE}
          component={TextField}
          type="text"
          label={t('consumer.eicxCode')}
          required
          disabled={!isAdmin && isEdit}
        />
      </Col>
    </Row>
    <Row gutter={32}>
      <Col span={12}>
        <Field
          name={consumerFields.PERSONAL_CODE}
          component={TextField}
          type="text"
          label={t('consumer.personalCodeLabel')}
          required
          parse={parseNumber}
          disabled={!isAdmin && isEdit}
        />
      </Col>
    </Row>
  </React.Fragment>
);

AddConsumerCompany.propTypes = {
  t: PropTypes.func.isRequired,
};

AddConsumerPerson.propTypes = {
  t: PropTypes.func.isRequired,
};

function nullifyRegistryCodeOrPersonalCode(formData) {
  if (formData.consumerType === consumerTypeClassificator.MANUFACTURER) {
    formData.personalCode = null;
  } else {
    formData.registrationCode = null;
  }
}

export class AddConsumer extends Component {
  static propTypes = {
    closeModal: PropTypes.func.isRequired,
    t: PropTypes.func.isRequired,
    isLoading: PropTypes.bool.isRequired,
    isAdmin: PropTypes.bool,
    legalEntityId: PropTypes.number,
    initialValues: PropTypes.object,
    addConsumer: PropTypes.func.isRequired,
  };

  onSubmit = async (values) => {
    const formData = { ...values };
    const legalEntityId = this.props.legalEntityId;
    nullifyRegistryCodeOrPersonalCode(formData);
    const result = await this.props.addConsumer({
      [consumerFields.ID]: uuidv4(),
      ...formData,
      legalEntityId,
    });
    if (result?.error) {
      return { [FORM_ERROR]: 'submitFailed' };
    }
    this.props.closeModal();
  };

  render() {
    const { t, isLoading, closeModal, isAdmin, initialValues } = this.props;
    const isEdit = !!initialValues?.[consumerFields.ID];

    const validate = makeRffValidateFromLegacy(
      validateSchema,
      this.props,
      initialValues
    );

    return (
      <Form
        onSubmit={this.onSubmit}
        validate={validate}
        initialValues={initialValues}
        initialValuesEqual={isEqual}
        render={({ handleSubmit, values }) => {
          const consumerType = values?.[consumerFields.CONSUMER_TYPE];
          const isCompany =
            !consumerType ||
            consumerType === consumerTypeClassificator.MANUFACTURER;
          return (
            <Spin spinning={isLoading}>
              <form className="form-add-consumer" onSubmit={handleSubmit}>
                <FormStateToRedux form={FORM_ADD_CONSUMER} />
                <Row gutter={32}>
                  <Col span={12}>
                    <Field
                      label={t('consumer.consumerType')}
                      name={consumerFields.CONSUMER_TYPE}
                      component={ClassificatorField}
                      classificatorType={ClassificatorField.types.consumerType}
                      hasFeedback={false}
                      required
                      disabled={!isAdmin && isEdit}
                    />
                  </Col>
                </Row>
                {isCompany && (
                  <AddConsumerCompany t={t} isAdmin={isAdmin} isEdit={isEdit} />
                )}
                {!isCompany && (
                  <AddConsumerPerson t={t} isAdmin={isAdmin} isEdit={isEdit} />
                )}
                <FormActions>
                  <Button
                    className="component-register-actions__button-previous"
                    size="large"
                    onClick={closeModal}
                  >
                    {t('consumer.buttonCancel')}
                  </Button>
                  <Button
                    className="component-register-actions__button-next"
                    size="large"
                    type="primary"
                    htmlType="submit"
                    disabled={!isAdmin && isEdit}
                  >
                    {isEdit
                      ? t('consumer.buttonSave')
                      : t('consumer.buttonAdd')}
                  </Button>
                </FormActions>
              </form>
            </Spin>
          );
        }}
      />
    );
  }
}

const getLoading = getLoadingSelector(getAddConsumer);

const mapStateToProps = (state) => ({
  isLoading: getLoading(state),
});

const mapDispatchToProps = {
  addConsumer,
};

export default withTranslation(NAMESPACES.consumer)(
  connect(mapStateToProps, mapDispatchToProps)(AddConsumer)
);
